import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import arrowLeft from '~/assets/icons/arrow_left.svg';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Select } from '~/components/ui/Select';
import { Text } from '~/components/ui/Text';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	InputAction,
	isRgbPaintType,
	VehiclePaintType,
	WalletType,
} from '~/shared';
import { useBuy, usePaintColors, usePaintPreview } from '../../hooks';
import {
	setActivePaintGroup,
	setActiveStage,
	setGroup,
	setIndexedColor,
	setPearlColor,
	setRimsColor,
	setSectionIndex,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CategoryImages, CategoryNames, CategoryTypes } from '../CategoryTab';
import { ColorTypeSelector } from '../ColorTypeSelector';
import { RGBPalette } from '../RGBPalette';
import {
	StyledButtonContainer,
	StyledButtonContent,
	StyledContainer,
	StyledSettingsContainer,
} from './PaintTab.styled';

const PaintTab: React.FC = () => {
	const dispatch = useLayoutDispatch();
	const paintGroups = useObservable(
		rage.vehiclePaintShop.getVehiclePaintTypes(),
	);
	const pearlColors = useObservable(
		rage.vehiclePaintShop.getVehiclePearlColors(),
	);
	const rimsColors = useObservable(
		rage.vehiclePaintShop.getVehicleRimsColors(),
	);
	const vehicleIndexedColors = useObservable(
		rage.vehiclePaintShop.getVehicleIndexedColors(),
	);
	const sectionIndex = useLayoutSelector(
		(state) => state.colorPicker.sectionIndex,
	);
	const activeCategory = useLayoutSelector(
		(state) => state.carPaint.activeCategory,
	);
	const activePaintGroup = useLayoutSelector(
		(state) => state.colorPicker.paintGroup,
	);
	const pearlColor = useLayoutSelector((state) => state.colorPicker.pearlColor);
	const indexedColor = useLayoutSelector(
		(state) => state.colorPicker.indexedColor,
	);
	const rimsColor = useLayoutSelector((state) => state.colorPicker.rimsColor);
	const ownedVehiclePaint = useLayoutSelector(
		(state) => state.carPaint.ownedVehiclePaint,
	);
	const paintGroup = useLayoutSelector((state) => state.colorPicker.paintGroup);
	const { colorList, activeColor } = usePaintColors();
	usePaintPreview();
	const { handleBuy } = useBuy();

	const handleBack = useCallback(() => {
		dispatch(setActiveStage('category'));
	}, []);

	const handleSelectPaintGroup = useCallback(
		(value: string | null) => {
			if (!value) {
				return paintGroups?.[0];
			}

			const selectedGroup = paintGroups.find((item) => item.name === value);
			if (!selectedGroup) return;

			dispatch(setActivePaintGroup(selectedGroup));
			dispatch(setGroup(selectedGroup));

			const isSpecialPaintType = [
				VehiclePaintType.Chameleon,
				VehiclePaintType.Pearl,
			].includes(selectedGroup.type);

			if (!isSpecialPaintType) {
				dispatch(setIndexedColor(0));
				return;
			}

			if (
				!isRgbPaintType(selectedGroup.type) &&
				selectedGroup.type !== VehiclePaintType.Chrome
			) {
				const colors = vehicleIndexedColors.filter(
					(item) => item.type === selectedGroup.type,
				);

				if (colors.length > 0) {
					const shouldUseOwnedColor =
						ownedVehiclePaint?.bodyColor &&
						ownedVehiclePaint.bodyColor.type === selectedGroup.type &&
						typeof ownedVehiclePaint.bodyColor.color === 'number';

					const initialIndexedColor = shouldUseOwnedColor
						? (ownedVehiclePaint!.bodyColor.color as number)
						: colors[0].index;

					dispatch(setIndexedColor(initialIndexedColor));
				}
			}

			const isPearl = selectedGroup.type === VehiclePaintType.Pearl;
			const isPaintingWheels = activeCategory === CategoryTypes.PaintingWheels;

			if (isPearl && isPaintingWheels && !rimsColor) {
				dispatch(setRimsColor(rimsColors[0]?.index || 0));
				return;
			}

			if (isPearl && !pearlColor) {
				dispatch(setPearlColor(pearlColors[0]?.index || 0));
				return;
			}
		},
		[
			vehicleIndexedColors,
			paintGroups,
			paintGroup,
			pearlColors,
			pearlColor,
			rimsColor,
			indexedColor,
			sectionIndex,
			activeCategory,
			activePaintGroup,
			rimsColors,
		],
	);

	const handleSelectColor = useCallback(
		(value: string | null) => {
			if (!value) {
				return;
			}

			const currentPaintType =
				activeCategory === CategoryTypes.PaintingWheels
					? VehiclePaintType.Pearl
					: activePaintGroup?.type;
			const isPearl = currentPaintType === VehiclePaintType.Pearl;

			if (!currentPaintType) {
				return;
			}

			if (isPearl) {
				if (activeCategory === CategoryTypes.PaintingWheels) {
					const selectedColor = rimsColors.find((item) => item.name === value);
					dispatch(setRimsColor(selectedColor?.index || 0));
					return;
				}

				const selectedColor = pearlColors.find((item) => item.name === value);
				dispatch(setPearlColor(selectedColor?.index || 0));
				return;
			}

			if (!isRgbPaintType(currentPaintType)) {
				const selectedColor = vehicleIndexedColors.find(
					(item) => item.name === value,
				);
				dispatch(setIndexedColor(selectedColor?.index || 0));
			}
		},
		[
			activePaintGroup,
			pearlColors,
			vehicleIndexedColors,
			activeCategory,
			sectionIndex,
			rimsColors,
		],
	);

	useInputAction(InputAction.CommonMenuAccept, () =>
		handleBuy(WalletType.Cash),
	);

	useEffectOnce(() => {
		dispatch(setSectionIndex(0));

		const pearlPaintGroup = paintGroups.find(
			(item) => item.type === VehiclePaintType.Pearl,
		);

		if (activeCategory === CategoryTypes.PaintingWheels) {
			dispatch(setGroup(pearlPaintGroup));

			if (!pearlColor) {
				const initialRimsColor = ownedVehiclePaint?.rimsColor
					? rimsColors.find(
							(item) => item.index === ownedVehiclePaint.rimsColor,
					  )
					: rimsColors[0];
				dispatch(setRimsColor(initialRimsColor?.index || 0));
			}
			return;
		}

		if (!activePaintGroup) {
			dispatch(setActivePaintGroup(paintGroups[0]));
			return;
		}

		if (activePaintGroup?.type === VehiclePaintType.Pearl && !pearlColor) {
			const initialPearlColor = ownedVehiclePaint?.bodyColor.pearlColor
				? pearlColors.find(
						(item) => item.index === ownedVehiclePaint.bodyColor.pearlColor,
				  )
				: pearlColors[0];
			dispatch(setPearlColor(initialPearlColor?.index || 0));
		}
	});

	if (!activeCategory) {
		return;
	}

	return (
		<StyledContainer>
			<SectionHeading
				icon={CategoryImages[activeCategory]}
				text={CategoryNames[activeCategory]}
			/>
			<StyledSettingsContainer>
				<ColorTypeSelector />
				{activeCategory !== CategoryTypes.PaintingWheels &&
					sectionIndex === 0 && (
						<Select
							items={paintGroups.map((item) => item.name)}
							value={activePaintGroup?.name || paintGroups[0].name}
							onChange={handleSelectPaintGroup}
						/>
					)}
				{colorList.length > 0 && sectionIndex === 0 && (
					<Select
						items={colorList.map((item) => item.name)}
						value={activeColor?.name as string}
						onChange={handleSelectColor}
					/>
				)}
			</StyledSettingsContainer>
			<RGBPalette />
			<StyledButtonContainer>
				<Button
					color={ButtonColors.Default}
					height={ButtonHeight.Height50}
					onClick={handleBack}
				>
					<StyledButtonContent>
						<img src={arrowLeft} />
						<Text>Вернуться назад</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonContainer>
		</StyledContainer>
	);
};

export { PaintTab };
